import { supabase } from '../lib/supabase';

// Define types for the nested objects returned by Supabase
interface Business {
  id: string;
  name: string;
}

interface Customer {
  id: string;
  first_name: string;
  last_name: string;
  email?: string;
  phone?: string;
}

// Define type for the booking with properly nested objects
interface BookingWithRelations {
  id: string;
  customer_id: string;
  business_id: string;
  status: string;
  drop_off_date: string | null;
  drop_off_time: string | null;
  businesses: Business;
  customers: Customer;
}

interface AutomationData {
  id: string;
  business_id: string;
  appointment_canceled_active: boolean;
  sms_action_active: boolean;
  sms_action_message?: string;
  email_action_active: boolean;
  email_action_subject?: string;
  email_action_body?: string;
  notify_person_active: boolean;
  notify_person_target?: 'all_resources' | 'admin' | 'custom';
  notify_person_phone?: string;
  notify_person_message?: string;
}

// Define return type that includes error property
interface ProcessResult {
  success: boolean;
  results?: any[];
  error?: unknown;
}

// Function to format time in AM/PM format
function formatTimeAMPM(timeStr: string): string {
  if (!timeStr) return '';
  
  try {
    // Handle different time formats
    let hours: number;
    let minutes: number;
    
    // Handle formats like "13:30:00" or "13:30"
    if (timeStr.includes(':')) {
      const [hoursStr, minutesStr] = timeStr.split(':');
      hours = parseInt(hoursStr, 10);
      minutes = parseInt(minutesStr, 10);
    } 
    // Handle format like "1330"
    else if (timeStr.length === 4) {
      hours = parseInt(timeStr.substring(0, 2), 10);
      minutes = parseInt(timeStr.substring(2), 10);
    }
    // Default case
    else {
      return timeStr; // Return original if we can't parse
    }
    
    // Convert to 12-hour format
    const ampm = hours >= 12 ? 'pm' : 'am';
    hours = hours % 12;
    hours = hours || 12; // Convert 0 to 12
    
    // Format the result
    return `${hours}${minutes > 0 ? `:${minutes.toString().padStart(2, '0')}` : ''}${ampm}`;
  } catch (e) {
    console.error('Error formatting time:', e);
    return timeStr; // Return original on error
  }
}

// Get active automations with the appointment_canceled trigger enabled for a business
async function getActiveAutomationsForBusiness(businessId: string): Promise<AutomationData[]> {
  if (!supabase) {
    console.error('Supabase client not initialized');
    return [];
  }

  try {
    const { data, error } = await supabase
      .from('automation_settings')
      .select('*')
      .eq('business_id', businessId)
      .eq('is_active', true)
      .eq('appointment_canceled_active', true);

    if (error) {
      console.error('Error fetching automations:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error('Error fetching automations:', error);
    return [];
  }
}

// Process a canceled booking by triggering appropriate automations
export async function processCanceledBooking(bookingId: string): Promise<ProcessResult> {
  if (!supabase) {
    console.error('Supabase client not initialized');
    return { success: false };
  }

  try {
    // Fetch detailed booking information
    const { data: booking, error: bookingError } = await supabase
      .from('bookings')
      .select(`
        id,
        customer_id,
        business_id,
        status,
        drop_off_date,
        drop_off_time,
        businesses:business_id (
          id,
          name
        ),
        customers:customer_id (
          id,
          first_name,
          last_name,
          email,
          phone
        )
      `)
      .eq('id', bookingId)
      .single();

    if (bookingError || !booking) {
      console.error('Error fetching booking:', bookingError);
      return { success: false };
    }

    // Convert the nested data structure from arrays to single objects
    // This is needed because Supabase returns joinable tables as arrays
    // even when there's only one item (in single() queries)
    const typedBooking: BookingWithRelations = {
      ...booking,
      businesses: Array.isArray(booking.businesses) ? booking.businesses[0] : booking.businesses,
      customers: Array.isArray(booking.customers) ? booking.customers[0] : booking.customers
    };

    // Verify the booking is actually canceled
    if (typedBooking.status !== 'cancelled') {
      console.log(`Booking ${bookingId} is not canceled (status: ${typedBooking.status}), skipping cancellation processing`);
      return { success: false };
    }
    
    // Now fetch booking services to get vehicle information
    const { data: services, error: servicesError } = await supabase
      .from('booking_services')
      .select(`
        id,
        service_id,
        vehicle_id,
        name,
        description,
        price,
        tax_exempt,
        tax_rate,
        tax_amount,
        total_amount,
        duration_minutes
      `)
      .eq('booking_id', bookingId);
    
    if (servicesError) {
      console.error('Error fetching booking services:', servicesError);
    }
    console.log(`Found ${services?.length || 0} services for booking ${bookingId}`);
    
    // Extract vehicle information from services
    let vehicle_make = '';
    let vehicle_model = '';
    let vehicle_year = '';
    let vehicle_color = '';
    let service_name = 'Appointment'; // Default value
    
    // Get service name if available
    if (services && services.length > 0) {
      service_name = services[0].name || 'Appointment';
      
      // Now try to get vehicle info if we have a vehicle_id
      if (services[0].vehicle_id) {
        try {
          console.log(`Looking up vehicle with ID: ${services[0].vehicle_id}`);
          
          // Try to get vehicle info from the vehicles table
          const { data: vehicleData, error: vehicleError } = await supabase
            .from('customer_vehicles')
            .select(`
              id,
              make,
              model,
              year,
              color
            `)
            .eq('id', services[0].vehicle_id)
            .single();
            
          if (vehicleError) {
            console.error('Error fetching vehicle data:', vehicleError);
          } else if (vehicleData) {
            console.log('Found vehicle data:', vehicleData);
            
            // Extract vehicle information
            vehicle_make = vehicleData.make || '';
            vehicle_model = vehicleData.model || '';
            vehicle_year = vehicleData.year ? vehicleData.year.toString() : '';
            vehicle_color = vehicleData.color || '';
          } else {
            console.log('No vehicle data found for vehicle_id:', services[0].vehicle_id);
          }
        } catch (vehicleLookupError) {
          console.error('Error during vehicle lookup:', vehicleLookupError);
        }
      } else {
        console.log('No vehicle_id found in service');
      }
    } else {
      console.log('No services found for booking - using empty vehicle information');
    }

    // Get active automations with appointment_canceled trigger for this business
    const automations = await getActiveAutomationsForBusiness(typedBooking.business_id);
    
    if (automations.length === 0) {
      console.log(`No active cancellation automations found for business ${typedBooking.business_id}`);
      return { success: true, results: [] };
    }

    // Prepare template data for SMS and email messages
    const templateData: Record<string, string> = {
      customer_first_name: typedBooking.customers.first_name || '',
      customer_last_name: typedBooking.customers.last_name || '',
      customer_full_name: `${typedBooking.customers.first_name || ''} ${typedBooking.customers.last_name || ''}`.trim(),
      drop_off_date_formatted: typedBooking.drop_off_date 
        ? new Date(typedBooking.drop_off_date).toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
            timeZone: 'UTC'
          })
        : '',
      drop_off_time_formatted: formatTimeAMPM(typedBooking.drop_off_time || ''),
      business_name: typedBooking.businesses.name || '',
      appointment_id: typedBooking.id,
      booking_id: typedBooking.id,
      booking_number: typedBooking.id.split('-')[0] || typedBooking.id, // Use first part of ID as a short booking number
      service_name,
      vehicle_make,
      vehicle_model,
      vehicle_year,
      vehicle_color,
      vehicle: (vehicle_make && vehicle_model) ? 
        `${vehicle_year ? vehicle_year + ' ' : ''}${vehicle_make} ${vehicle_model}${vehicle_color ? ' (' + vehicle_color + ')' : ''}` : 
        'No vehicle information',
      phoneNumber: typedBooking.customers.phone || '',
      email: typedBooking.customers.email || '',
      drop_off_date: typedBooking.drop_off_date || '',
      drop_off_time: typedBooking.drop_off_time || '',
      status: typedBooking.status,
      status_formatted: 'Cancelled', // Explicit format for cancellations
      cancel_date: new Date().toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'UTC'
      }),
      cancel_time: new Date().toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: 'numeric',
        hour12: true,
        timeZone: 'UTC'
      })
    };

    // Import the actions for direct execution
    const { SMS_ACTION, EMAIL_ACTION, NOTIFY_PERSON_ACTION } = await import('../lib/automations/actions');
    
    const results = [];

    // Process each automation
    for (const automation of automations) {
      // Process SMS action
      if (automation.sms_action_active && typedBooking.customers.phone) {
        try {
          // Set the message on the action instance
          SMS_ACTION.settings = {
            ...SMS_ACTION.settings,
            message: automation.sms_action_message || ''
          };
          
          // Log detailed information about the SMS being sent
          console.log('============ SENDING CANCELLATION SMS ============');
          console.log(`Booking ID: ${typedBooking.id}`);
          console.log(`Customer: ${typedBooking.customers.first_name} ${typedBooking.customers.last_name}`);
          console.log(`Phone: ${typedBooking.customers.phone}`);
          console.log(`Original appointment: ${templateData.drop_off_date_formatted} at ${templateData.drop_off_time_formatted}`);
          console.log(`Template: ${automation.sms_action_message}`);
          console.log('=================================================');
          
          // Execute the SMS action
          console.log(`Executing SMS cancellation for booking ${bookingId}`);
          await SMS_ACTION.execute(typedBooking.business_id, templateData);
          
          // Log the action
          await supabase.from('automation_action_log').insert({
            business_id: typedBooking.business_id,
            automation_id: automation.id,
            action_type: 'sms',
            recipient: typedBooking.customers.phone,
            trigger_type: 'appointment_canceled',
            trigger_data: {
              booking_id: typedBooking.id,
              original_date: typedBooking.drop_off_date,
              original_time: typedBooking.drop_off_time,
              cancel_timestamp: new Date().toISOString(),
              vehicle_make,
              vehicle_model,
              vehicle_year,
              vehicle_color,
              service_name
            }
          });
          
          results.push({ type: 'sms', automation_id: automation.id, success: true });
        } catch (error) {
          console.error('Error executing SMS action:', error);
          results.push({ type: 'sms', automation_id: automation.id, success: false, error });
        }
      }
      
      // Process Email action
      if (automation.email_action_active && typedBooking.customers.email) {
        try {
          // Set the email template on the action instance
          EMAIL_ACTION.settings = {
            ...EMAIL_ACTION.settings,
            subject: automation.email_action_subject || '',
            emailBody: automation.email_action_body || ''
          };
          
          // Log detailed information about the email being sent
          console.log('============ SENDING CANCELLATION EMAIL ============');
          console.log(`Booking ID: ${typedBooking.id}`);
          console.log(`Customer: ${typedBooking.customers.first_name} ${typedBooking.customers.last_name}`);
          console.log(`Email: ${typedBooking.customers.email}`);
          console.log(`Original appointment: ${templateData.drop_off_date_formatted} at ${templateData.drop_off_time_formatted}`);
          console.log(`Subject template: ${automation.email_action_subject}`);
          console.log(`Body template: ${automation.email_action_body}`);
          console.log('===================================================');
          
          // Execute the Email action
          console.log(`Executing Email cancellation for booking ${bookingId}`);
          await EMAIL_ACTION.execute(typedBooking.business_id, templateData);
          
          // Log the action
          await supabase.from('automation_action_log').insert({
            business_id: typedBooking.business_id,
            automation_id: automation.id,
            action_type: 'email',
            recipient: typedBooking.customers.email,
            trigger_type: 'appointment_canceled',
            trigger_data: {
              booking_id: typedBooking.id,
              original_date: typedBooking.drop_off_date,
              original_time: typedBooking.drop_off_time,
              cancel_timestamp: new Date().toISOString(),
              vehicle_make,
              vehicle_model,
              vehicle_year,
              vehicle_color,
              service_name
            }
          });
          
          results.push({ type: 'email', automation_id: automation.id, success: true });
        } catch (error) {
          console.error('Error executing Email action:', error);
          results.push({ type: 'email', automation_id: automation.id, success: false, error });
        }
      }
      
      // Process Notify Person action
      if (automation.notify_person_active) {
        try {
          // Set the notify settings on the action instance
          NOTIFY_PERSON_ACTION.settings = {
            ...NOTIFY_PERSON_ACTION.settings,
            notifyTarget: automation.notify_person_target || 'custom',
            notifyPhoneNumber: automation.notify_person_phone || '',
            notifyMessage: automation.notify_person_message || ''
          };
          
          // Log detailed information about the notification being sent
          console.log('============ SENDING CANCELLATION NOTIFICATION ============');
          console.log(`Booking ID: ${typedBooking.id}`);
          console.log(`Target type: ${automation.notify_person_target || 'custom'}`);
          console.log(`Custom phone: ${automation.notify_person_phone || 'Not set'}`);
          console.log(`Original appointment: ${templateData.drop_off_date_formatted} at ${templateData.drop_off_time_formatted}`);
          console.log(`Message template length: ${(automation.notify_person_message || '').length} chars`);
          console.log(`Vehicle information: ${templateData.vehicle}`);
          console.log('=========================================================');
          
          // Execute the Notify Person action
          console.log(`Executing Notify Person action for canceled booking ${bookingId}`);
          await NOTIFY_PERSON_ACTION.execute(typedBooking.business_id, templateData);
          
          // Log the action
          await supabase.from('automation_action_log').insert({
            business_id: typedBooking.business_id,
            automation_id: automation.id,
            action_type: 'notify_person',
            recipient: automation.notify_person_phone || 'unknown',
            trigger_type: 'appointment_canceled',
            trigger_data: {
              booking_id: typedBooking.id,
              original_date: typedBooking.drop_off_date,
              original_time: typedBooking.drop_off_time,
              cancel_timestamp: new Date().toISOString(),
              vehicle_make,
              vehicle_model,
              vehicle_year,
              vehicle_color,
              service_name
            }
          });
          
          results.push({ type: 'notify_person', automation_id: automation.id, success: true });
        } catch (error) {
          console.error('Error executing Notify Person action:', error);
          results.push({ type: 'notify_person', automation_id: automation.id, success: false, error });
        }
      }
    }
    
    console.log(`Successfully processed cancellation for booking ${bookingId}`, {
      results,
      customer: `${typedBooking.customers.first_name} ${typedBooking.customers.last_name}`,
      appointment: typedBooking.drop_off_date ? `${typedBooking.drop_off_date} ${typedBooking.drop_off_time}` : 'No date set'
    });
    
    return { success: true, results };
  } catch (error) {
    console.error('Error processing canceled booking:', error);
    return { success: false, error };
  }
} 